// The two FullCast packages, in one place.
//
// /pricing and every /proposal/:slug page read from here so a proposal can
// only ever describe one of the two standard packages, in the same words as
// the pricing sheet. There is no third option and no per-client variant: a
// proposal picks a tier key, and everything about what that tier includes
// comes from this file.
//
// The Growth Partnership list is also the /podcast-production offer. Keep it
// in sync with INCLUDED in PodcastProduction.tsx: they are the same product.

export type OfferKey = "growth_partnership" | "production"

export type Offer = {
  key: OfferKey
  name: string
  label: string
  summary: string
  price: string
  term: string
  setup: string
  clientOwns: string
}

// Every episode on both tiers is one fully produced weekly video episode.
// Audio-only is not offered.
export const CADENCE = "One fully produced video episode every week"
export const LAUNCH = "About six weeks from kickoff to the first episode, starting with a trailer"

export const PARTNERSHIP: [string, string][] = [
  ["Authority strategy", "the show's business role, audience and positioning, decided before anything gets recorded"],
  ["Relationship map", "the categories of people worth building around: future clients, referral partners, collaborators"],
  ["Guest pipeline intelligence", "researched, prioritized guest opportunities each month, with context and outreach templates"],
  ["Conversation system", "host briefs, guest notes, interview questions and episode angles"],
  ["Weekly video production", "polished weekly video episodes, fully edited, published and packaged"],
  ["Content multiplication", "every conversation repurposed into show notes, clips, posts and assets that outlive the episode"],
  ["Monthly strategic review", "content momentum, guest strategy and relationship opportunities, looked at together"],
]

export const PRODUCTION = [
  "Weekly video editing and mastering",
  "Show notes, timestamps, pull quotes",
  "Per-episode artwork",
  "Published to your site, SEO-optimized",
  "Distributed to every directory",
  "Cross-posted to your social channels",
  "Guest promo email drafted for each episode",
  "Monthly performance report",
]

// What every episode gets on both tiers.
export const EVERY_EPISODE = [
  "Weekly video episode: full edit, mastering, intro/outro and music",
  "Show notes with summary, timestamps, pull quotes and links",
  "Custom artwork built from your guest's headshot",
  "Episode page on your site, SEO-optimized, player embedded",
  "Distribution to Apple, Spotify and every other directory",
  "Cross-posting to LinkedIn, YouTube and your other channels",
  "Guest promo email, written and ready to send",
]

export const SETUP = [
  "Launch strategy and format consultation",
  "Cover art design",
  "Intro and outro music selection",
  "Voice-over script and talent guidance",
  "Equipment recommendations and training",
  "Hosting platform selection and setup",
  "Trailer episode produced with you",
  "Submission to every major directory",
  "Podcast section built on your website",
  "Coaching on format, interviews and scheduling",
]

export const OFFERS: Record<OfferKey, Offer> = {
  growth_partnership: {
    key: "growth_partnership",
    name: "Podcast Growth Partnership",
    label: "The full partnership",
    summary:
      "For established experts who want the show to build authority and relationships, not just publish episodes.",
    price: "$5,000",
    term: "Six-month engagement",
    setup: "plus $5,000 one-time setup — reduced for existing shows",
    clientOwns:
      "showing up to record, approving guests, sending the invitations, and the follow-up. Those land better coming from you.",
  },
  production: {
    key: "production",
    name: "Podcast Production",
    label: "Production retainer",
    summary:
      "For hosts who already know what the show is for, and want the production and publishing lifted off their plate.",
    price: "$3,000",
    term: "Month to month",
    setup: "plus $5,000 one-time setup — reduced for existing shows",
    clientOwns: "the show's direction, who's on it, and everything that happens off the mic.",
  },
}

// The tier's inclusion list as plain lines, for pages that list it.
export function inclusions(key: OfferKey): string[] {
  return key === "growth_partnership"
    ? PARTNERSHIP.map(([name, detail]) => `${name}: ${detail}`)
    : PRODUCTION
}
